/**
 * Live auction room — layout catalog + preference resolution (pure).
 *
 * SHARED. This lived in `apps/web` until 2026-08-05, when the native app needed
 * the same ten layouts. The app reads and writes the SAME
 * `profiles.live_room_layout` value, so the catalog and the precedence rules
 * have to be one implementation: a bidder who picks Gallery Wall on their phone
 * must not find Closing Reel on the website, and an id one surface writes must
 * be an id the other recognises.
 *
 * The ten interchangeable live-room layouts (Claude Design "Refined" bundle)
 * plus the logic for deciding which one a bidder sees. Kept free of DOM and of
 * React Native so both platforms can test the precedence rules directly.
 *
 * Preference precedence (highest first):
 *   1. the bidder's ACCOUNT choice (`profiles.live_room_layout`) — follows them
 *      across devices when signed in;
 *   2. the device-local value (`localStorage`) — carries the choice for a
 *      signed-out bidder, or before the account row has loaded;
 *   3. the default.
 * Unknown / legacy values are ignored at each level (fall through to the next).
 */

/**
 * 🔴 NO `A · ` PREFIX. Jim, 2026-09-16: *"Remove all of the letters next to the
 * view names."*
 *
 * The letters came from the design bundle, where they indexed ten artboards.
 * They were never anything to a bidder — the phone had already been stripping
 * them at the point of render (`l.label.replace(/^[A-J] · /, '')`, twice, with a
 * comment calling them "a designer's index and not something to read to a
 * bidder"). Removing them HERE means the label is the name on both surfaces and
 * neither has to launder it.
 *
 * ⚠️ These are LABELS, not ids. The `id` is the persisted value in
 * `profiles.live_room_layout`, the key in each renderer map and the key in
 * `LAYOUT_PAGINATES` — it does not move, and a stored preference survives.
 */
export const LIVE_ROOM_LAYOUTS = [
  { id: 'reel', label: 'Closing Reel' },
  { id: 'ticker', label: 'Ticker + Grid' },
  { id: 'focused', label: 'Focused Watch' },
  { id: 'sortable', label: 'Sortable Grid' },
  { id: 'watchlist', label: 'Watchlist Rail' },
  { id: 'split', label: 'Split Compare' },
  { id: 'schedule', label: 'Schedule Timeline' },
  { id: 'gallery', label: 'Gallery Wall' },
  { id: 'spotlight', label: 'Spotlight Theater' },
  { id: 'category', label: 'Category Columns' },
] as const;

export type LiveRoomLayoutId = (typeof LIVE_ROOM_LAYOUTS)[number]['id'];

export const DEFAULT_LIVE_ROOM_LAYOUT: LiveRoomLayoutId = 'reel';

/**
 * Device-local fallback key.
 *
 * `localStorage` on the web; the native app stores it under the same name in
 * its own key-value store, so the constant stays shared even though the storage
 * behind it does not.
 */
export const LIVE_ROOM_LAYOUT_STORE_KEY = 'auctisync.liveroom.layout';

/** Return the value only when it is a known layout id, else null. */
export function normalizeLayoutId(value: unknown): LiveRoomLayoutId | null {
  return LIVE_ROOM_LAYOUTS.find((l) => l.id === value)?.id ?? null;
}

/**
 * Resolve the layout to show on load. Account preference wins (cross-device),
 * then the device-local value, then the default.
 */
export function resolveInitialLayout(accountValue: unknown, localValue: unknown): LiveRoomLayoutId {
  return (
    normalizeLayoutId(accountValue) ?? normalizeLayoutId(localValue) ?? DEFAULT_LIVE_ROOM_LAYOUT
  );
}
